import {Component, EventEmitter, Input, Output} from '@angular/core';
import {HttpErrorResponse, HttpStatusCode} from "@angular/common/http";
import {ActivatedRoute, Router} from "@angular/router";
import {CustomMessageService, LocalStorageService, ResponseHttpError, ResponseHttpSuccess, UtilService, OriginMessageError} from "library";
import {RequestService} from "projects/store/src/app/services/api/v1/request.service";
import {PathNavigationService} from 'projects/store/src/app/services/api/v1/path-navigation.service';
import {SalesWorkflowService} from "projects/store/src/app/services/api/v1/sales-workflow.service";
import {LocalStorageKey, RequestCancelReason, SalesWorkflow, SalesWorkflowSteps} from "projects/store/src/app/enums";
import {ErrorTemplate} from "projects/store/src/app/interfaces";
import {AuthService} from "projects/store/src/app/services/api/v1/auth.service";
import {PixelService} from "../../../../services/api/v1/pixel.service";
import {MacroFlowService} from 'projects/store/src/app/services/api/v1/macro-flow.service';
import {SalesWorkflowMacro} from 'projects/store/src/app/enums/sales-workflow-steps.enum';
import {environment} from "projects/store/src/environments/environment";

interface Error {
  code: string, path: string, image: boolean, title: string, message: string, optionalMessage?: string,
  button?: { label: string, icon: boolean, iconPos?: 'left' | 'right', routerLink?: string, method?: () => void,}
}

enum ErrorType {
  'INIC' = 'INIC',
  'INLN' = 'INLN',
  'INFR' = 'INFR',
  'INAF' = 'INAF',
  'INIV' = 'INIV',
  'INOS' = 'INOS',
  'INIA' = 'INIA',
}

@Component({
  selector: 'app-sales-client-verification-status',
  templateUrl: './sales-client-verification-status.component.html'
})
export class SalesClientVerificationStatusComponent {
  requestId: string;
  sessionId: string;
  timeout: number = 5000;
  maxRetries = 3;
  status: number = HttpStatusCode.Processing;
  typeError!: ErrorType;
  interval: any;
  httpResponseStatusCodes: any;
  data: any;
  flowCode: string;
  loading = false;
  isHomeDeliveryFlow: boolean = false;
  showHomeDeliverySuccess: boolean = false;
  whatsappPhone!: string;

  @Input() response: any;
  @Input() resume!: boolean;
  @Output() nextStepEvent = new EventEmitter();

  error!: Error;

  constructor(private route: ActivatedRoute,
              private router: Router,
              private localStorageService: LocalStorageService,
              private customMessageService: CustomMessageService,
              private utilService: UtilService,
              private requestService: RequestService,
              public salesWorkflowService: SalesWorkflowService,
              private authService: AuthService,
              private pixelService: PixelService,
              private pathNavigationService: PathNavigationService,
              private macroFlowService: MacroFlowService) {
    this.requestId = this.route.snapshot.params['request_id']
    this.flowCode = this.route.snapshot.params['flow_code']
    this.sessionId = this.route.snapshot.queryParams['session_id']
    this.httpResponseStatusCodes = {
      PARTIAL_CONTENT: HttpStatusCode.PartialContent,
      OK: HttpStatusCode.Ok,
      NOT_ACCEPTABLE: HttpStatusCode.NotAcceptable,
      CONFLICT: HttpStatusCode.Conflict,
      GONE: HttpStatusCode.Gone
    };

    this.status = HttpStatusCode.PartialContent;
  }

  ngOnInit() {
    this.getStatusValidation();

    this.flowCode = this.route.snapshot.params['flow_code'];
    const firstFlow = this.macroFlowService.getFlowByPositionInMacro(SalesWorkflowMacro['macro-venta-domicilio'], 0);
    this.isHomeDeliveryFlow = this.flowCode == firstFlow;

    this.whatsappPhone = this.getWhatsAppPhone();
  }

  getStatusValidation() {
    this.requestService.getClientVerificationStatus(this.requestId, { session_id: this.sessionId }).subscribe({
      next: (resp: ResponseHttpSuccess) => this.confirm(resp),
      error: (error: ResponseHttpError) => this.confirm(error)
    });
  }

  confirm(resp: any) {
    this.utilService.disablePageLoading();
    let reasonText: string;
    // resp.status = HttpStatusCode.ResetContent // TEST MGP
    const errorCode = resp?.error?.data?.error_code ?? 'UNKNOWN_ERROR';
    switch(resp.status) {
      case HttpStatusCode.PartialContent:
        setTimeout(()=> this.getStatusValidation(), this.timeout)
        this.status = resp.status;
        break;
      case HttpStatusCode.Ok:
        if (this.salesWorkflowService.workflow.code !== this.macroFlowService.getKredicashClienteFlow()){
          // this.setLocalStorage();
          if (this.isHomeDeliveryFlow) {
            this.showHomeDeliverySuccess = true;
            this.status = resp.status;
          } else {
            this.onNextStep();
          }
          return;
        }
          this.status = resp.status;
        break;
      case HttpStatusCode.NotAcceptable:
        reasonText = this.authService.isAuthenticated() ? RequestCancelReason.SELLER_CANCELS  : RequestCancelReason.CUSTOMER_CANCELS ;
        this.pixelService.trackEvent('RejectedApplication');
        this.cancelRequest(reasonText);
        clearInterval(this.interval);
        const errorCode =  resp.error.data.code;
        this.onShowError(errorCode);
        this.customMessageService.responseNoticeHandler({...resp, error: { ...resp.error, message: resp.error?.data?.description}}, OriginMessageError.BACKEND, this.error);
        break;
      case HttpStatusCode.Conflict:
        reasonText = RequestCancelReason.CUSTOMER_VERIFICATION_ISSUE;
        this.pixelService.trackEvent('RejectedApplication');
        this.cancelRequest(reasonText, (reasonText) => this.handleRequestNotApproved(reasonText));
        this.status = resp.status;
        break;
      case HttpStatusCode.Gone:
        this.status = resp.status;
        break;
      default:
        this.customMessageService.responseNoticeHandler(resp);
    }
  }

  onShowError(error_code: string) {
    switch (error_code) {
      case ErrorType.INIC:
        this.error = this.getErrorObject(
          error_code,
          'Hemos encontrado un inconveniente con tu documento',
          'Asegurate de que hayas introducido tus datos personales correctamente.'
        );
        break;

      case ErrorType.INLN:
        this.error = this.getErrorObject(
          error_code,
          'Hubo un inconveniente con tu biometría',
          'Asegúrate de que la foto de tu documento y tu selfie sean nítidas.'
        );
        break;

      case ErrorType.INFR:
        this.error = this.getErrorObject(
          error_code,
          'Hubo un inconveniente con tu documento y selfie',
          'Asegúrate de que la fotografía de tu documento coincida con la de tu selfie y que ambas sean nítidas.'
        );
        break;

      case ErrorType.INAF:
        this.error = this.getErrorObject(
          error_code,
          'Hubo un inconveniente en tu proceso de validación',
          'Asegúrate de que los datos ingresados coincidan con los de tu documento personal.'
        );
        break;

      case ErrorType.INIA:
        this.error = this.getErrorObject(
          error_code,
          $localize`:@@sales.clientVerification.status.errorInValidationTitle:Hubo un inconveniente en tu validación`,
          $localize`:@@sales.clientVerification.status.errorAgeMessage:Asegúrate de ser mayor de edad para poder acceder a un crédito con nosotros.`
        );
        break;

      case ErrorType.INIV:
        this.error = this.getErrorObject(
          error_code,
          $localize`:@@sales.clientVerification.status.errorInValidationTitle:Hubo un inconveniente en tu validación`,
          $localize`:@@sales.clientVerification.status.errorAgeMessage:Asegúrate de ser mayor de edad para poder acceder a un crédito con nosotros.`
        );
        break;

      case ErrorType.INIV:
        this.error = this.getErrorObject(
          error_code,
          'Hubo un inconveniente en tu validación',
          'Asegúrate que tu documento de identidad este vigente para continuar con el proceso',
        );
        break;

      case ErrorType.INOS:
        this.error = this.getErrorObject(
          error_code,
          'Hubo un inconveniente en el sistema',
          'Por favor, intenta nuevamente en 10 minutos. Estamos presentando fallas técnicas.'
        );
        break;
      default:
        this.error = this.getErrorObject(
          error_code,
          'No pudimos completar la validación',
          'Ocurrió un inconveniente inesperado. Por favor, vuelve a intentarlo en unos minutos. Si el problema persiste, contáctanos para ayudarte.'
        );
        break;
      }
  }

  getErrorObject(error_code: string, title: string, message: string): any {
    return { code: '', path: '', image: true, title, message, optionalMessage: `Código de asesor: ${error_code}`, button: {label: 'Reintentar', icon: false, ...this.salesWorkflowService.getInitialPath(this.flowCode)},optionCopy: true,messageCopy: { url: this.utilService.getCurrentUrl(), error: { message }  }  };
  }


  setLocalStorage() {
    let data = this.localStorageService.get(LocalStorageKey.salesData);
    data = { ...data, request: {id: this.requestId }}
    if(this.route.snapshot.queryParams['external_site'] !== undefined) {
      Object.assign(data, { external_site: this.route.snapshot.queryParams['external_site']});
      Object.assign(data, { external_callback_url: this.route.snapshot.queryParams['external_callback_url']});
    }
    this.localStorageService.set(LocalStorageKey.salesData, data);
  }

  cancelRequest(reasonText: string, onNotApproved?: (reason: string) => void) {
    this.requestService.cancel(this.requestId, reasonText).subscribe({
      next: (resp: ResponseHttpSuccess) => {
        if (resp.status !== HttpStatusCode.NoContent) {
          this.customMessageService.responseNoticeHandler(resp);
        } else {
          if (onNotApproved) {
            onNotApproved(reasonText);
          }
        }
      },
      error: (err: HttpErrorResponse) => {
        this.customMessageService.responseNoticeHandler(err);
      }
    });
  }

  onNextStep() {
    this.loading = true;
    this.pixelService.trackEvent('Lead');
    this.localStorageService.remove(LocalStorageKey.onboardingStarted);
    this.nextStepEvent.emit();
  }

  private handleRequestNotApproved(messageError: string): void {
    const attemptedUrl = this.utilService.getCurrentUrl();
    const state = { attemptedUrl, messageError };

    this.pathNavigationService.navigateToRequestNotApproved(this.flowCode, state);
  }

  getWhatsAppPhone(): string {

    const phoneNumber = this.requestService.request?.['store']?.phone ?? '';

    if (phoneNumber && phoneNumber.length !== '') {
      const phoneCountryMask = environment.global.country.phoneCountryMask;
      const cleanPhone = phoneNumber.replace(/^\+/, '').replace(/\D/g, '');
      return this.utilService.formatPhoneNumber(cleanPhone, phoneCountryMask);
    }

    // Valor por defecto si no hay número de teléfono
    return '';
  }

  getWhatsAppLink(): string {
    const phone = this.whatsappPhone.replace(/[^0-9]/g, '');
    return `https://wa.me/${phone}`;
  }

  protected readonly ErrorType = ErrorType;
}
